// The mandate a card is saved under.
//
// A saved card is charged later with nobody present — at the end of a free
// trial, at every renewal — so saving one needs the cardholder's agreement to
// exactly that, recorded. POST /Payment/setup-intents takes it as `consent`, and
// the server stores who agreed, when, from which address, and which version of
// this wording they were shown.
//
// That makes the version a promise about the text. The template below is what
// version CONSENT_TEXT_VERSION says, and consent.test.js pins the two together:
// change a word of the template and the test fails until the version moves on
// too. Never edit the text under a version that has already shipped — agreements
// were recorded against it.
//
// TODO(legal): the wording is the frontend's first draft, modelled on Stripe's
// recommended text for saving a card for future payments. Have it reviewed.

export const CONSENT_TEXT_VERSION = "2026-10-v3";

// Every plan is charged monthly, the annual one too: it is a 12-month
// commitment billed monthly (2026-10-14). So the agreement names one interval,
// and an annual plan's adds what the commitment means for cancelling it and
// for moving to a monthly plan. Version 2026-09-v1 said "billed every year"
// for an annual plan, which stopped being true then.
//
// The card is charged by the company, not the product: the agreement names it
// as Stripe's public details do (legal name, then public name), which is who the
// receipts show. Version 2026-10-v2 named "KAPsurvey" instead (2026-10-20).
export const CONSENT_TEMPLATE =
   "I authorise International Advisory, Products and Systems Ltd (i-APS), which provides " +
   "KAPsurvey, to save this card and to charge it, without asking me each time, " +
   "for the KAPsurvey subscription of {organization}: {plan}, billed every month at the " +
   "plan's price plus any applicable taxes. The first charge is made when the free trial ends, " +
   "or straight away if a payment is already due, and then at the start of each billing month " +
   "until the subscription ends.{commitment} I can change the plan or cancel at any time from " +
   "Subscription settings.";

/** Added to an annual plan's agreement, where the template says {commitment}. */
export const CONSENT_COMMITMENT =
   " The annual plan is a 12-month commitment: if I cancel it before its last month, it stays " +
   "active and is charged every month until the 12 months end, and it can move to a monthly plan " +
   "only in its last month.";

/** "month" or "year", the two intervals the API takes. "year" is the annual plan. */
export const consentInterval = (isMonthly) => (isMonthly === false ? "year" : "month");

/**
 * The agreement as it is shown.
 *
 * `planLabel` is how the plan is named on its card ("the Standard plan
 * ($99/month)"); left out when the plan could not be looked up, rather than
 * guessed. `interval` "year" is the annual plan, which adds the commitment.
 */
export const renderConsentText = ({ organizationName, planLabel, interval }) =>
   CONSENT_TEMPLATE.replace("{organization}", organizationName || "this organization")
      .replace("{plan}", planLabel || "its current plan")
      .replace("{commitment}", interval === "year" ? CONSENT_COMMITMENT : "");

/**
 * The `consent` block for POST /Payment/setup-intents.
 *
 * `amountShown` is what saving the card will charge at once, as billing-status
 * reported it (`amountDue.amount`, in cents, tax included) and the card form
 * showed it. The server checks it against its own figure and refuses a stale
 * one (409 CONSENT_AMOUNT_MISMATCH). Nothing is owed — a trial, a card update on
 * a subscription that is paid up — when it is 0, and the server then skips the
 * check and still records the agreement. A plan waiting for its first card is
 * sent 0 too: billing-status reports no amount for it, and the packet price the
 * plan cards show excludes tax.
 */
export const buildConsent = ({ interval, amountShown = 0 }) => ({
   accepted: true,
   textVersion: CONSENT_TEXT_VERSION,
   amountShown: Number.isFinite(amountShown) && amountShown > 0 ? Math.round(amountShown) : 0,
   interval: interval === "year" ? "year" : "month"
});
